"use client";

import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment-timezone";
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronDown,
  ChevronUp,
  MapPin,
  X,
} from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { Button } from "@baluu/ui/components/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@baluu/ui/components/card";
import { Calendar } from "@baluu/ui/components/calendar";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";
import { slotPoolFit } from "../../lib/checkout/pool-availability";

// Types
type PricingOption = {
  id: string;
  name: string;
  description?: string;
  price: number;
  /**
   * Optional: absent when the listing gives this ticket no single dependable
   * length. `checkout-appointment` leaves it undefined rather than printing
   * the first-wins guess, which would contradict the window the header, the
   * footer and the basket row all resolve from the SELECTED date's slots.
   */
  duration?: number;
  ticketsIds?: string[];
};

type AvailableSlot = {
  startMinutes: number;
  endMinutes: number;
  startUTC?: string;
  endUTC?: string;
  optionIndex: number;
  hasPublicSession?: boolean;
  spotsRemaining?: number;
  totalSpots?: number;
  publicSessionId?: string;
  /** Effective location for this slot (PartnerLocation id, "-1"/absent = no location). */
  locationId?: string;
  /**
   * Equipment left on this slot's pool, for the ticket the server was asked
   * about. Absent = no pool or never asked, which reads as uncapped.
   */
  poolMaxTickets?: number;
  /** Unrounded free units on that pool; preferred over poolMaxTickets. */
  poolFreeUnits?: number;
};

type AvailableDate = {
  dateISO: string;
  isDisabled: boolean;
  availableSlots: {
    startMinutes: number;
    endMinutes: number;
    startUTC?: string;
    endUTC?: string;
    scheduleSlotId: string;
    ticketIndex: number;
    locationId?: string;
    poolMaxTickets?: number;
    poolFreeUnits?: number;
    // Public-session annotations the API adds when a slot already has one.
    // Declared because the mapping below has always read them; without these
    // the reads were seven standing type errors.
    hasPublicSession?: boolean;
    spotsRemaining?: number;
    totalSpots?: number;
    publicSessionId?: string;
  }[];
};

type NextAvailableDateInfo = {
  dateISO: string;
  month: number;
  year: number;
};

type AppointmentTimeSlotStepProps = {
  course: any;
  pricingOptions: PricingOption[];
  timezone?: string;
  currency?: string;
  selectedDate: Date | null;
  selectedTime: number | null;
  selectedOption: number | null;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (time: number | null) => void;
  onOptionChange: (option: number | null) => void;
  availableDates?: AvailableDate[];
  isLoading?: boolean;
  onMonthChange?: (month: Date) => void;
  currentMonth?: Date;
  className?: string;
  nextAvailableDateInfo?: NextAvailableDateInfo | null;
  isSearchingNextAvailable?: boolean;
  /** When true, hide the pricing options picker (V2 handles ticket selection in a separate step) */
  hidePricingOptions?: boolean;
  /**
   * Which array the listing keeps its schedule slots in — the server keys this
   * on `isGroupSchedule`, NOT on `isV2` (GetCourseAvailableSlots.controller.js
   * :646-650). Without it a group listing's slots never match their configured
   * slot, every slot falls to `optionIndex: 0`, and the options step can only
   * ever offer `pricingOptions[0]` — the most expensive, since that array is
   * sorted price DESC.
   */
  isGroupSchedule?: boolean;
  /**
   * The ticket id this response was FETCHED with, or null if it was not
   * filtered.
   *
   * ⚠ When the server filters by ticket it renumbers `ticketIndex` within the
   * filtered array (`GetCourseAvailableSlots.controller.js:694-700`), so
   * indexing the configured array with it lands on a different ticket
   * entirely. Every slot in a filtered response belongs to this one ticket, so
   * its option is resolved from the id instead of from the index.
   */
  filteredForTicketTypeId?: string | null;
  /**
   * How many of the pool-bound ticket the customer has ALREADY chosen.
   *
   * Non-zero only when the quantity step runs before the slot step — the
   * variable-duration order. 0 (the default) constrains nothing, which is what
   * the fixed-duration order needs on its first pass through the grid.
   */
  requestedPoolQuantity?: number;
  /**
   * The same demand in POOL UNITS, when the measured ticket has a pool. Used
   * with each slot's unrounded poolFreeUnits so a mixed-unit basket is not
   * refused by the rounding in poolMaxTickets.
   */
  requestedPoolUnits?: number | null;
  /** Name map for every effective location in the schedule, keyed by PartnerLocation id. */
  locationsById?: Record<string, { name: string }>;
  /** Distinct locations across the whole schedule — a filter dropdown is shown when there are 2+ */
  locationOptions?: { id: string; name: string }[];
  /** Active location filter (PartnerLocation id; null/undefined = all locations) */
  locationFilter?: string | null;
  /** Called when the user changes or clears the location filter */
  onChangeLocationFilter?: (locationId: string | null) => void;
  /**
   * Whether the listing's schedule spans 2+ distinct locations (computed
   * schedule-wide by the API). Gates the per-slot location labels —
   * single-location listings render their slots unchanged.
   */
  hasMultipleLocations?: boolean;
  /**
   * Effective "location first" mode from the API. "required" replaces the
   * optional filter pill with a gating Location card: Date/Time are not
   * rendered until `locationFilter` is set. Always "optional" from the API
   * when the schedule has < 2 locations.
   */
  locationSelectionMode?: "optional" | "required";
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
};

function formatPrice(price: number, currency = "GBP"): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${(price / 100).toFixed(2)}`;
}

function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}hr`;
  }
  return `${hours}hr ${mins}min`;
}

function AppointmentTimeSlotStep({
  course,
  pricingOptions,
  timezone,
  currency = "GBP",
  selectedDate,
  selectedTime,
  selectedOption,
  onDateChange,
  onTimeChange,
  onOptionChange,
  availableDates = [],
  isLoading = false,
  onMonthChange,
  currentMonth,
  className,
  nextAvailableDateInfo,
  isSearchingNextAvailable = false,
  hidePricingOptions = false,
  isGroupSchedule = false,
  filteredForTicketTypeId = null,
  requestedPoolQuantity = 0,
  requestedPoolUnits = null,
  locationsById = {},
  locationOptions,
  locationFilter = null,
  onChangeLocationFilter,
  hasMultipleLocations = false,
  locationSelectionMode = "optional",
}: AppointmentTimeSlotStepProps) {
  const [isLocationOpen, setIsLocationOpen] = useState(true);
  const [isDateOpen, setIsDateOpen] = useState(true);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);

  // Gate only when there is something to choose. The API already collapses
  // "required" to "optional" for < 2 locations; this is the UI's own guard so
  // a stale/old response can never hide the calendar behind an empty card.
  const isLocationGated =
    locationSelectionMode === "required" &&
    !!onChangeLocationFilter &&
    (locationOptions?.length ?? 0) >= 2;
  const selectedLocationOption = locationOptions?.find(
    (o) => o.id === locationFilter,
  );
  const showDateAndTime = !isLocationGated || !!locationFilter;

  // Get available slots for selected date
  useEffect(() => {
    if (!selectedDate || availableDates.length === 0) {
      setAvailableSlots([]);
      return;
    }

    // Use course timezone for proper date parsing (critical for courses in different timezones)
    const courseTimezone = course?.timezone || timezone || "Europe/London";

    // Parse selected date in course timezone to get the correct date string
    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth();
    const selectedDay = selectedDate.getDate();
    const selectedDateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`;

    const dateData = availableDates.find((d) => {
      // Parse dateISO in the COURSE timezone, not browser timezone
      // This ensures dates are compared correctly for courses in different timezones (e.g., New Zealand)
      const dStr = moment.tz(d.dateISO, courseTimezone).format("YYYY-MM-DD");
      return dStr === selectedDateStr;
    });

    if (!dateData || dateData.isDisabled || !dateData.availableSlots) {
      setAvailableSlots([]);
      return;
    }

    // Map API slots to available slots with pricing option indices
    const slots: AvailableSlot[] = dateData.availableSlots.map((slot) => {
      // Find the schedule slot from course data
      // ⚠ Stringified on both sides: the id is an ObjectId server-side and a
      // string once serialised, and the course payload has been seen both ways.
      const configuredSlots = isGroupSchedule
        ? course?.groupScheduleSlots
        : course?.appointmentScheduleSlots;
      const scheduleSlot = configuredSlots?.find(
        (s: any) =>
          s?._id != null && String(s._id) === String(slot.scheduleSlotId),
      );

      // Public session info from the API (if present). poolMaxTickets rides
      // along here rather than in each branch: all three returns spread this
      // object, so one line reaches every one of them.
      const publicSessionInfo = {
        ...(slot.hasPublicSession && { hasPublicSession: true }),
        ...(slot.spotsRemaining !== undefined && { spotsRemaining: slot.spotsRemaining }),
        ...(slot.totalSpots !== undefined && { totalSpots: slot.totalSpots }),
        ...(slot.publicSessionId && { publicSessionId: slot.publicSessionId }),
        ...(typeof slot.poolMaxTickets === "number" && {
          poolMaxTickets: slot.poolMaxTickets,
        }),
        ...(typeof slot.poolFreeUnits === "number" && {
          poolFreeUnits: slot.poolFreeUnits,
        }),
      };

      // ⚠ Before the `!scheduleSlot` guard, because it does not need one:
      // it resolves the option from the ticket id, not from the schedule
      // slot. Below the guard, a slot whose `scheduleSlotId` is missing
      // from the course payload — edited or deleted between the course
      // fetch and the availability fetch — fell to `optionIndex: 0`, the
      // most EXPENSIVE option, and `uniqueTimeSlots` then dropped it.
      //
      // A ticket-filtered response cannot be read by index — the server
      // renumbers `ticketIndex` within the filtered array. Every slot in one
      // belongs to the ticket it was filtered for, so the option comes from
      // that id. Reading the index here mapped slots to another ticket's
      // option, and `uniqueTimeSlots` filters on `optionIndex`, so the slot the
      // customer had just picked could disappear from the grid.
      if (filteredForTicketTypeId) {
        const filteredOptionIndex = pricingOptions.findIndex(
          (option) => String(option.id) === String(filteredForTicketTypeId),
        );
        return {
          startMinutes: slot.startMinutes,
          endMinutes: slot.endMinutes,
          startUTC: slot.startUTC,
          endUTC: slot.endUTC,
          optionIndex: filteredOptionIndex >= 0 ? filteredOptionIndex : 0,
          locationId: slot.locationId,
          ...publicSessionInfo,
        };
      }

      if (!scheduleSlot) {
        return {
          startMinutes: slot.startMinutes,
          endMinutes: slot.endMinutes,
          startUTC: slot.startUTC,
          endUTC: slot.endUTC,
          optionIndex: 0,
          locationId: slot.locationId,
          ...publicSessionInfo,
        };
      }

      // Get the ticket at the specified index
      const ticket = scheduleSlot.tickets?.[slot.ticketIndex];

      if (!ticket) {
        return {
          startMinutes: slot.startMinutes,
          endMinutes: slot.endMinutes,
          startUTC: slot.startUTC,
          endUTC: slot.endUTC,
          optionIndex: 0,
          locationId: slot.locationId,
          ...publicSessionInfo,
        };
      }

      // Find which pricing option contains this ticket
      // Match by ticket.ticketTypes (contains ticket type IDs) OR ticket._id (for backwards compatibility)
      // ⚠ Stringified on both sides, like the `scheduleSlotId` match above.
      // A raw comparison here fails silently and worse: `optionIndex` falls
      // back to 0, and `pricingOptions` is sorted price DESC, so every slot
      // would map to the most EXPENSIVE option — the same failure the
      // `isGroupSchedule` prop exists to prevent.
      const ticketTypeIds = (ticket.ticketTypes || []).map(String);
      const slotTicketId = ticket._id == null ? null : String(ticket._id);
      const optionIndex = pricingOptions.findIndex(
        (option) =>
          ticketTypeIds.includes(String(option.id)) ||
          (slotTicketId !== null &&
            (option.ticketsIds || []).some(
              (id: any) => String(id) === slotTicketId,
            )),
      );

      return {
        startMinutes: slot.startMinutes,
        endMinutes: slot.endMinutes,
        startUTC: slot.startUTC,
        endUTC: slot.endUTC,
        optionIndex: optionIndex >= 0 ? optionIndex : 0,
        locationId: slot.locationId,
        ...publicSessionInfo,
      };
    });

    setAvailableSlots(slots);
  }, [
    isGroupSchedule,
    filteredForTicketTypeId,
    course?.groupScheduleSlots,
    selectedDate,
    availableDates,
    course?.appointmentScheduleSlots,
    pricingOptions,
  ]);

  // Get unique time slots
  const uniqueTimeSlots = useMemo(() => {
    if (selectedOption !== null) {
      return availableSlots.filter(
        (slot) => slot.optionIndex === selectedOption,
      );
    }
    // Remove duplicates by start time
    const seen = new Set<number>();
    return availableSlots.filter((slot) => {
      if (seen.has(slot.startMinutes)) return false;
      seen.add(slot.startMinutes);
      return true;
    });
  }, [availableSlots, selectedOption]);

  // Resolve a slot's locationId to a known location name, treating "-1",
  // undefined, and ids absent from locationsById as "no label".
  const resolveLocationName = useCallback(
    (locationId: string | undefined) => {
      if (!locationId || locationId === "-1") return undefined;
      return locationsById[locationId]?.name;
    },
    [locationsById],
  );

  // Get available pricing options for selected time
  const optionsForSelectedTime = useMemo(() => {
    if (selectedTime === null) return [];

    const optionIndices = new Set<number>();
    availableSlots
      .filter((slot) => slot.startMinutes === selectedTime)
      .forEach((slot) => optionIndices.add(slot.optionIndex));

    return pricingOptions.filter((_, index) => optionIndices.has(index));
  }, [selectedTime, availableSlots, pricingOptions]);

  // Handle date change
  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      setIsDateOpen(false);
      setIsTimeOpen(true);
      onDateChange(date || null);
      onTimeChange(null);
      onOptionChange(null);
    },
    [onDateChange, onTimeChange, onOptionChange],
  );

  // Handle time change
  const handleTimeChange = useCallback(
    (time: number) => {
      onTimeChange(time);
      setIsTimeOpen(false);

      // Get options available for this time
      const optionIndices = new Set<number>();
      availableSlots
        .filter((slot) => slot.startMinutes === time)
        .forEach((slot) => optionIndices.add(slot.optionIndex));

      // V2: always auto-select first option (ticket selection happens in separate step)
      if (hidePricingOptions) {
        onOptionChange([...optionIndices][0] ?? 0);
        setIsOptionsOpen(false);
      } else if (optionIndices.size === 1) {
        // Auto-select if only one option
        onOptionChange([...optionIndices][0]!);
        setIsOptionsOpen(false);
      } else {
        setIsOptionsOpen(true);
      }
    },
    [availableSlots, onTimeChange, onOptionChange, hidePricingOptions],
  );

  // Handle option change
  const handleOptionChange = useCallback(
    (optionIndex: number) => {
      onOptionChange(optionIndex);
      setIsOptionsOpen(false);
    },
    [onOptionChange],
  );

  // Check if a date is disabled
  const isDateDisabled = useCallback(
    (date: Date) => {
      // Use course timezone for proper date parsing
      const courseTimezone = course?.timezone || timezone || "Europe/London";

      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

      const dateData = availableDates.find((d) => {
        // Parse dateISO in the COURSE timezone, not browser timezone
        const dStr = moment.tz(d.dateISO, courseTimezone).format("YYYY-MM-DD");
        return dStr === dateStr;
      });
      return !dateData || dateData.isDisabled;
    },
    [availableDates, course?.timezone, timezone],
  );

  // Format selected date
  const formattedDate = useMemo(() => {
    if (!selectedDate) return "Choose the date you'd like to book.";
    return selectedDate.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [selectedDate]);

  // Format selected time
  const formattedTime = useMemo(() => {
    if (selectedTime === null) return "Choose the time you'd like to book.";

    // ⚠ Every slot at this start, not `find`'s arbitrary first one. The server
    // emits one slot per TICKET from the same `startMinutes`, each with its own
    // `endMinutes`, so a 09:30 that exists only because a 30-minute trim steps
    // there can be picked by a customer who then chooses the 60-minute cut.
    // `find` printed the trim's end, and now that the basket and final screen
    // show a per-ticket window this header would contradict them. When the
    // slots at a start disagree there is no end to name yet — the ticket step
    // has not run — so name only the start.
    const slotsAtStart = availableSlots.filter(
      s => s.startMinutes === selectedTime,
    );
    // Once an option IS chosen, the slot that produced it is the one to read —
    // every slot carries the `optionIndex` it was mapped from. Falling back to
    // the whole set only matters before that choice exists, which is when the
    // ambiguity below is real rather than merely unresolved.
    // ⚠ Only when the customer actually CHOSE it. Under `hidePricingOptions`
    // the option is auto-assigned by `handleTimeChange` as
    // `[...optionIndices][0] ?? 0` — whichever slot the server emitted first —
    // so narrowing by it would reinstate the arbitrary end this memo removed,
    // and disagree with the basket once the tickets step runs.
    const narrowingByOption = selectedOption !== null && !hidePricingOptions;
    // ⚠ No fallback to the whole set. `handleLocationFilterChange` keeps
    // `selectedTime` and `selectedOptionIndex` whenever ANY slot survives at
    // that start — it matches on `startMinutes` alone — so the chosen option's
    // slot can be gone while the selection stands. Falling back then printed
    // another ticket's end beside a duration the resolver had narrowed by the
    // chosen one. An empty narrowing means there is no end to name, which the
    // start-alone path below already says.
    const candidates = narrowingByOption
      ? slotsAtStart.filter(s => s.optionIndex === selectedOption)
      : slotsAtStart;
    const lengths = new Set(
      candidates
        .map(s => s.endMinutes - s.startMinutes)
        .filter(d => Number.isFinite(d) && d > 0),
    );
    if (lengths.size === 1) {
      return `${minutesToTime(selectedTime)} - ${minutesToTime(selectedTime + [...lengths][0]!)}`;
    }
    if (lengths.size > 1) return minutesToTime(selectedTime);

    // No slot at this start — reachable for the render between a refetch
    // dropping it and the effect clearing the time.
    //
    // ⚠ Name the START ALONE, always. This used to derive an end from
    // `pricingOptions[].duration`, which is `durationMap.get(...) ?? tt.duration
    // ?? 60` — so the "end" it printed was the fabricated hour this whole
    // change exists to stop showing, and a guard on `duration <= 0` never
    // caught it because 60 is not zero. There is no slot to size this from; the
    // ambiguous branch above says so, and so does this one.
    return minutesToTime(selectedTime);
  }, [selectedTime, selectedOption, availableSlots, hidePricingOptions]);

  // Selected slot's location name — only surfaced when the listing has
  // multiple distinct locations (single-location listings never compute or
  // render this).
  const selectedSlotLocationName = useMemo(() => {
    if (!hasMultipleLocations || selectedTime === null) return undefined;
    const selectedSlot = availableSlots.find(
      (s) => s.startMinutes === selectedTime,
    );
    return resolveLocationName(selectedSlot?.locationId);
  }, [hasMultipleLocations, selectedTime, availableSlots, resolveLocationName]);

  // Handle "Next Available" button click - navigates to month and selects the date
  const handleNextAvailable = useCallback(() => {
    if (!nextAvailableDateInfo) return;

    const courseTimezone = course?.timezone || timezone || "Europe/London";

    // Parse the date in the course timezone and extract date components
    // to avoid timezone shifting when converting to JS Date
    const m = moment.tz(nextAvailableDateInfo.dateISO, courseTimezone);
    const year = m.year();
    const month = m.month(); // 0-indexed
    const day = m.date();

    // Create a local date with just the date components (no time shifting)
    const dateObj = new Date(year, month, day);

    // Navigate to the month with available dates
    if (onMonthChange) {
      onMonthChange(dateObj);
    }

    // Select the date
    onDateChange(dateObj);

    // Close date picker and open time picker
    setIsDateOpen(false);
    setIsTimeOpen(true);
  }, [
    nextAvailableDateInfo,
    course?.timezone,
    timezone,
    onMonthChange,
    onDateChange,
  ]);

  // Format the next available date for display
  const nextAvailableDateFormatted = useMemo(() => {
    if (!nextAvailableDateInfo) return null;
    const courseTimezone = course?.timezone || timezone || "Europe/London";
    return moment
      .tz(nextAvailableDateInfo.dateISO, courseTimezone)
      .format("D MMM YYYY");
  }, [nextAvailableDateInfo, course?.timezone, timezone]);

  return (
    <div className={cn("space-y-4", className)}>
      <CheckoutSteps.Header
        title="Select Date & Time"
        description="Choose your preferred appointment slot."
      />

      {/* Location filter — only when the schedule spans 2+ locations */}
      {!isLocationGated &&
        onChangeLocationFilter &&
        locationOptions &&
        locationOptions.length >= 2 && (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={locationFilter ?? "all"}
              onValueChange={(value) =>
                onChangeLocationFilter(value === "all" ? null : value)
              }
            >
              {/* Styled as a secondary pill — matches date-selection-step's filter */}
              <SelectTrigger className="h-8 w-auto gap-2 rounded-md border-0 bg-secondary text-secondary-foreground text-sm font-medium shadow-none hover:bg-secondary/80">
                <MapPin className="w-4 h-4" />
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              {/* container={null} + z-[10002]: renders inline so the dropdown
                  works inside the checkout overlay AND the webcomponents
                  Shadow DOM — matches date-selection-step. */}
              <SelectContent container={null} className="z-[10002]">
                <SelectItem value="all">All locations</SelectItem>
                {locationOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {locationFilter && (
              <div className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full bg-muted text-sm">
                <span className="text-muted-foreground">At</span>
                <span className="font-medium">
                  {locationOptions.find((o) => o.id === locationFilter)?.name ??
                    "Location"}
                </span>
                <button
                  type="button"
                  onClick={() => onChangeLocationFilter(null)}
                  className="ml-1 p-0.5 rounded-full hover:bg-background/60"
                  aria-label="Clear location filter"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        )}

      {/* Location gate — "location first" mode. Same collapsible card idiom as
          Date/Time; options are buttons (no Select → nothing to fight in the
          webcomponents Shadow DOM). Choosing goes through the SAME filter
          handler as the optional pill, so the keep-or-clear-selection logic
          lives in one place (the flow's handleLocationFilterChange). */}
      {isLocationGated && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-card-hover/50 transition-colors"
            onClick={() => setIsLocationOpen(!isLocationOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Location</CardTitle>
                <CardDescription
                  className={cn(
                    selectedLocationOption && "font-semibold text-foreground",
                  )}
                >
                  {selectedLocationOption?.name ??
                    "Choose a location to see dates"}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  isLocationOpen &&
                    "bg-green-100 dark:bg-green-900-muted text-green-500",
                )}
              >
                <MapPin className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          {isLocationOpen && (
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {locationOptions!.map((o) => (
                  <Button
                    key={o.id}
                    type="button"
                    variant={o.id === locationFilter ? "default" : "outline"}
                    className="justify-start"
                    onClick={() => {
                      onChangeLocationFilter!(o.id);
                      setIsLocationOpen(false);
                      setIsDateOpen(true);
                    }}
                  >
                    {o.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Date Selection Card */}
      {showDateAndTime && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-card-hover/50 transition-colors"
            onClick={() => setIsDateOpen(!isDateOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Date</CardTitle>
                <CardDescription
                  className={cn(
                    selectedDate && "font-semibold text-foreground",
                  )}
                >
                  {formattedDate}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  isDateOpen && "bg-green-100 dark:bg-green-900-muted text-green-500",
                )}
              >
                <CalendarIcon className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          {isDateOpen && (
            <CardContent className="pt-0 space-y-4 px-0! sm:px-3!">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground" />
                  <span className="ml-3 text-muted-foreground">
                    Loading available dates...
                  </span>
                </div>
              ) : (
                <>
                  <div className="w-full flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedDate || undefined}
                      onSelect={handleDateChange}
                      onMonthChange={onMonthChange}
                      month={currentMonth}
                      disabled={isDateDisabled}
                      captionLayout="dropdown"
                      startMonth={new Date()}
                      endMonth={
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 2),
                        )
                      }
                      className="w-full max-w-[320px] sm:max-w-none sm:w-auto rounded-md border p-2 sm:p-3 [--cell-size:36px] sm:[--cell-size:40px]"
                      classNames={{
                        months: "w-full sm:w-auto",
                        month: "w-full sm:w-auto",
                        weekdays: "w-full",
                        weekday: "w-[calc(100%/7)] text-center",
                        week: "w-full",
                        day: "w-[calc(100%/7)]",
                        today:
                          "bg-primary/10 text-primary font-medium rounded-md",
                      }}
                    />
                  </div>

                  {/* Next Available Button */}
                  {isSearchingNextAvailable ? (
                    <div className="flex items-center justify-center text-muted-foreground text-sm py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                      Finding available dates...
                    </div>
                  ) : nextAvailableDateInfo ? (
                    <div className="flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextAvailable}
                        className="border-primary text-primary hover:bg-primary/20"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Next Available: {nextAvailableDateFormatted}
                      </Button>
                    </div>
                  ) : null}
                </>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Time Selection Card */}
      {showDateAndTime && selectedDate && (
        <Card>
          <CardHeader
            className={cn(
              "transition-colors",
              uniqueTimeSlots.length > 0 &&
                "cursor-pointer hover:bg-card-hover/50",
            )}
            onClick={() =>
              uniqueTimeSlots.length > 0 && setIsTimeOpen(!isTimeOpen)
            }
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Time</CardTitle>
                <CardDescription
                  className={cn(
                    selectedTime !== null && "font-semibold text-foreground",
                  )}
                >
                  {formattedTime}
                  {selectedSlotLocationName && (
                    <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                      {selectedSlotLocationName}
                    </span>
                  )}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                disabled={uniqueTimeSlots.length === 0}
                className={cn(
                  isTimeOpen &&
                    "bg-green-100 dark:bg-green-900-muted text-green-500",
                )}
              >
                <Clock className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>

          {isTimeOpen && (
            <CardContent className="pt-0">
              {uniqueTimeSlots.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {uniqueTimeSlots.map((slot) => {
                    const slotLocationName = hasMultipleLocations
                      ? resolveLocationName(slot.locationId)
                      : undefined;
                    // Equipment, not seats. A slot the server returned always
                    // has at least one unit left (it drops the zeros), so this
                    // only ever refuses the "fewer than you asked for" band —
                    // and it stays open entirely when nothing is known.
                    const poolFit = slotPoolFit({
                      poolMaxTickets: slot.poolMaxTickets,
                      poolFreeUnits: slot.poolFreeUnits,
                      requestedQuantity: requestedPoolQuantity,
                      requestedUnits: requestedPoolUnits,
                    });
                    return (
                      <Button
                        key={slot.startMinutes}
                        variant={
                          slot.startMinutes === selectedTime
                            ? "default"
                            : "outline"
                        }
                        disabled={!poolFit.fits}
                        onClick={() => handleTimeChange(slot.startMinutes)}
                        className="w-full flex flex-col items-center gap-0.5 h-auto py-2"
                      >
                        <span>
                          {minutesToTime(slot.startMinutes)} -{" "}
                          {minutesToTime(slot.endMinutes)}
                        </span>
                        {slot.hasPublicSession && slot.spotsRemaining !== undefined && (
                          <span className="text-[10px] font-normal opacity-80">
                            {slot.spotsRemaining} {slot.spotsRemaining === 1 ? "spot" : "spots"} left
                          </span>
                        )}
                        {!poolFit.fits && poolFit.remaining !== null && (
                          <span className="text-[10px] font-normal opacity-80">
                            Only {poolFit.remaining} left
                          </span>
                        )}
                        {slotLocationName && (
                          <span className="text-[10px] font-normal opacity-80">
                            {slotLocationName}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground/70 mb-3" />
                  <p className="text-muted-foreground font-medium">
                    No times available
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Please select a different date
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Pricing Options Card — hidden for V2 (ticket selection in separate step) */}
      {selectedTime !== null && optionsForSelectedTime.length > 1 && !hidePricingOptions && (
        <Card>
          <CardHeader
            className="cursor-pointer hover:bg-card-hover/50 transition-colors"
            onClick={() => setIsOptionsOpen(!isOptionsOpen)}
          >
            <div className="flex items-center justify-between w-full">
              <div>
                <CardTitle className="text-base">Pricing Options</CardTitle>
                <CardDescription>
                  {selectedOption !== null
                    ? pricingOptions[selectedOption]?.name ||
                      "Select a pricing option"
                    : "Select the ticket type you'd like to book."}
                </CardDescription>
              </div>
              <Button variant="ghost" size="icon">
                {isOptionsOpen ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </Button>
            </div>
          </CardHeader>

          {isOptionsOpen && (
            <CardContent className="pt-0 space-y-3">
              {optionsForSelectedTime.map((option, i) => {
                const optionIndex = pricingOptions.findIndex(
                  (o) => o === option,
                );
                const isSelected = selectedOption === optionIndex;

                return (
                  <div
                    key={option.id}
                    onClick={() => handleOptionChange(optionIndex)}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all",
                      isSelected
                        ? "border-primary bg-primary/20"
                        : "border-border hover:border-muted-foreground/50",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-foreground">
                          {option.name}
                        </h4>
                        {option.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {option.description}
                          </p>
                        )}
                        {/* Nothing when the length is unknowable — see
                            `PricingOption.duration`. Printing the first-wins
                            guess here contradicted the window shown in the
                            header, the footer and the basket row. */}
                        {option.duration != null && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {formatDuration(option.duration)}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">
                          {formatPrice(option.price, currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

export { AppointmentTimeSlotStep };
export type {
  AppointmentTimeSlotStepProps,
  PricingOption,
  AvailableSlot,
  AvailableDate,
};
